// src/components/MyOrders.js - Updated for cart orders with HomePage UI
import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
} from "@mui/material";
import {
  ArrowBack,
  ShoppingCart,
  CheckCircle,
  Pending,
  Receipt,
  Refresh,
  Cancel,
  ExpandMore,
} from "@mui/icons-material";
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { db } from "../firebase/config";

// Import HomePage UI components
import NavBar from "./NavBar";
import CartDrawer from "./CartDrawer";
import UserMenu from "./UserMenu";
import Footer from "./Footer";
import FloatingCartButton from "./FloatingCartButton";
import Notifications from "./Notifications";
import { brandPalette as primaryBlue } from "../config/brandTheme";

const MyOrders = ({
  user,
  onBack,
  userProfile,
  // HomePage UI props
  cart = { packages: [], items: [] },
  removeFromCart = () => {},
  updateCartQuantity = () => {},
  formatPrice = (price) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(price),
  handleSignOut = () => {},
  onAdminPanel = () => {},
  isAdmin = false,
}) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // HomePage UI states
  const [anchorEl, setAnchorEl] = useState(null);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [categories] = useState(["All"]);
  const [expandedOrders, setExpandedOrders] = useState({});

  // HomePage styles

  const glassStyles = {
    background: "rgba(255, 255, 255, 0.25)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.18)",
    borderRadius: "16px",
    boxShadow: "0 8px 32px rgba(11, 28, 72, 0.37)",
  };

  const cardGlassStyles = {
    background: "rgba(255, 255, 255, 0.15)",
    backdropFilter: "blur(15px)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "20px",
    boxShadow: "0 8px 32px rgba(11, 28, 72, 0.2)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "&:hover": {
      transform: "translateY(-8px)",
      boxShadow: "0 16px 48px rgba(11, 28, 72, 0.35)",
      background: "rgba(255, 255, 255, 0.25)",
    },
  };

  // HomePage UI functions
  const handleMenu = (event) => {
    // Since we're already on MyOrders page, clicking profile icon should go back to home
    if (onBack) {
      onBack();
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCartDrawerClose = () => {
    setCartDrawerOpen(false);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ open: false, message: "" });
  };

  const getCartItemCount = () => {
    return (
      cart.packages.reduce((sum, p) => sum + p.quantity, 0) +
      cart.items.reduce((sum, i) => sum + i.quantity, 0)
    );
  };

  // GST calculation function for sub-items - GST is calculated on BASE PRICE ONLY
  const calculateSubItemGST = (subItem) => {
    const packagePrice = subItem.itemPrice || 0; // This is the selling/package price (base + profit)
    const basePrice = subItem.basePrice || 0; // Use basePrice for GST calculation
    const profitMargin = subItem.profitMargin || 0; // Profit portion
    const gstPercentage = (subItem.gstPercentage !== undefined && subItem.gstPercentage !== null && subItem.gstPercentage !== '') ? Number(subItem.gstPercentage) : 18;
    
    // GST should only be calculated on the base price, not on profit margin
    const gstAmount = basePrice > 0 ? (basePrice * gstPercentage) / 100 : 0;
    
    // Final price = base price + profit + GST (on base price only)
    const totalWithGST = basePrice + profitMargin + gstAmount;
    
    return {
      basePrice: basePrice,
      profitMargin: profitMargin,
      packagePrice: packagePrice,
      gstPercentage: gstPercentage,
      gstAmount: gstAmount,
      totalWithGST: totalWithGST
    };
  };

  const getCartTotal = () => {
    const packageTotal = cart.packages.reduce(
      (sum, pkg) => {
        // For packages with sub-items, calculate GST properly
        if (pkg.hasSubItems && pkg.selectedSubItems && Object.keys(pkg.selectedSubItems).length > 0) {
          let subItemsTotal = 0;
          Object.entries(pkg.selectedSubItems).forEach(([subItemId, isSelected]) => {
            if (isSelected) {
              const subItem = pkg.subItems?.find(item => item.id.toString() === subItemId);
              if (subItem) {
                const quantity = pkg.subItemQuantities?.[subItemId] || 1;
                const gstInfo = calculateSubItemGST(subItem);
                subItemsTotal += gstInfo.totalWithGST * quantity;
              }
            }
          });
          return sum + (subItemsTotal * pkg.quantity);
        } 
        // For packages with sub-items that use selectedSubItemsList (array format)
        else if (pkg.isSubItemPackage && pkg.selectedSubItemsList && pkg.selectedSubItemsList.length > 0) {
          let subItemsTotal = 0;
          pkg.selectedSubItemsList.forEach((subItem) => {
            const quantity = subItem.selectedQuantity || 1;
            const gstInfo = calculateSubItemGST(subItem);
            subItemsTotal += gstInfo.totalWithGST * quantity;
          });
          return sum + (subItemsTotal * pkg.quantity);
        }
        // Regular packages WITHOUT sub-items - NO GST, use price as-is
        else {
          return sum + pkg.price * pkg.quantity;
        }
      },
      0
    );
    const itemTotal = cart.items.reduce(
      (sum, item) => sum + item.sellingPrice * item.quantity,
      0
    );
    return packageTotal + itemTotal;
  };

  const canAddMoreToCart = (item, currentCartQuantity) => {
    const stockLimit = item.stockQuantity || 0;
    const maxOrderLimit = item.maxOrderQuantity || Infinity;
    const maxQuantity = Math.min(stockLimit, maxOrderLimit);
    return currentCartQuantity < maxQuantity;
  };

  // Function to format order ID consistently
  const formatOrderId = (longId) => {
    if (longId && longId.includes("CART-")) {
      const timestamp = longId.split("-")[1];
      const shortTimestamp = timestamp.slice(-6);
      return `CART${shortTimestamp}`;
    } else {
      return longId || "UNKNOWN";
    }
  };

  // Fetch user's orders from cartOrders collection
  const fetchMyOrders = async () => {
    try {
      setLoading(true);
      setError("");

      // Query cartOrders collection instead of orders
      const ordersQuery = query(
        collection(db, "cartOrders"),
        where("buyerId", "==", user.uid),
        orderBy("createdAt", "desc")
      );

      const snapshot = await getDocs(ordersQuery);
      const ordersData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setOrders(ordersData);
    } catch (error) {
      setError("Failed to fetch orders: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyOrders();
  }, [user.uid]);

  const formatDate = (dateString) => {
    if (!dateString) return "Processing...";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusInfo = (order) => {
    switch (order.orderStatus) {
      case "pending":
        return {
          chip: (
            <Chip label="Payment Pending" color="warning" icon={<Pending />} />
          ),
          message:
            "We're processing your payment. It will show as confirmed shortly after Cashfree verifies it.",
          color: "warning",
        };
      case "confirmed":
        return {
          chip: (
            <Chip
              label="Order Confirmed"
              color="success"
              icon={<CheckCircle />}
            />
          ),
          message:
            "Your order has been confirmed! Your items will be shipped soon.",
          color: "success",
        };
      case "rejected":
        return {
          chip: <Chip label="Order Rejected" color="error" />,
          message:
            order.rejectionReason ||
            "Your order has been rejected. Please contact support for more information.",
          color: "error",
        };
      case "cancelled":
        return {
          chip: <Chip label="Cancelled" color="error" />,
          message: "This order has been cancelled.",
          color: "error",
        };
      default:
        return {
          chip: <Chip label={order.orderStatus} color="default" />,
          message: "Order status is being updated.",
          color: "info",
        };
    }
  };

  const getOrderSummary = (order) => {
    const itemCount = order.orderCounts?.totalItems || 0;
    const packageCount = order.orderCounts?.totalPackages || 0;
    const uniqueItems = order.orderCounts?.uniqueItems || 0;
    const uniquePackages = order.orderCounts?.uniquePackages || 0;

    if (itemCount > 0 && packageCount > 0) {
      return `${uniqueItems} items (${itemCount} qty) + ${uniquePackages} packages (${packageCount} qty)`;
    } else if (itemCount > 0) {
      return `${uniqueItems} items (${itemCount} qty)`;
    } else if (packageCount > 0) {
      return `${uniquePackages} packages (${packageCount} qty)`;
    }
    return "Mixed order";
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, 
          ${primaryBlue[50]} 0%, 
          ${primaryBlue[100]} 25%, 
          ${primaryBlue[200]} 50%, 
          ${primaryBlue[100]} 75%, 
          ${primaryBlue[50]} 100%)`,
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 50%, rgba(0, 114, 206, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(11, 28, 72, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 80%, rgba(0, 34, 102, 0.1) 0%, transparent 50%)
          `,
          zIndex: 0,
        },
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1 }}>
        {/* NavBar Component */}
        <NavBar
          user={user}
          handleMenu={handleMenu}
          searchQuery={searchQuery}
          setSearchQuery={() => {}}
          selectedCategory={selectedCategory}
          setSelectedCategory={() => {}}
          categories={categories}
          cartItemCount={getCartItemCount()}
          primaryBlue={primaryBlue}
          glassStyles={glassStyles}
          setCartDrawerOpen={setCartDrawerOpen}
          showSearch={false} // Hide search in My Orders
          userProfile={userProfile}
        />

        {/* FloatingCartButton Component */}
        <FloatingCartButton
          getCartItemCount={getCartItemCount}
          setCartDrawerOpen={setCartDrawerOpen}
          primaryBlue={primaryBlue}
          glassStyles={glassStyles}
        />

        {/* CartDrawer Component */}
        <CartDrawer
          cartDrawerOpen={cartDrawerOpen}
          handleCartDrawerClose={handleCartDrawerClose}
          cart={cart}
          getCartItemCount={getCartItemCount}
          getCartTotal={getCartTotal}
          removeFromCart={removeFromCart}
          updateCartQuantity={updateCartQuantity}
          canAddMoreToCart={canAddMoreToCart}
          formatPrice={formatPrice}
          primaryBlue={primaryBlue}
          glassStyles={glassStyles}
          cardGlassStyles={cardGlassStyles}
          setShowCart={() => {}}
          setCartDrawerOpen={setCartDrawerOpen}
        />

        {/* Notifications Component */}
        <Notifications
          snackbar={snackbar}
          handleSnackbarClose={handleSnackbarClose}
          primaryBlue={primaryBlue}
          glassStyles={glassStyles}
        />

        {/* UserMenu Component */}
        <UserMenu
          anchorEl={anchorEl}
          handleClose={handleClose}
          isAdmin={isAdmin}
          onAdminPanel={onAdminPanel}
          handleSignOut={handleSignOut}
          setShowMyOrders={() => {}}
          setShowPackageStore={() => {}}
          setCartDrawerOpen={setCartDrawerOpen}
          setShowProfileSettings={() => {}}
          getCartItemCount={getCartItemCount}
          setDeleteDialogOpen={() => {}}
          deleteDialogOpen={false}
          handleDeleteAccount={() => {}}
          loading={false}
          primaryBlue={primaryBlue}
          glassStyles={glassStyles}
        />

        {/* Main Content */}
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 4,
              ...glassStyles,
              p: { xs: 2, sm: 3 },
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <IconButton
              onClick={onBack}
              sx={{
                mr: { xs: 1, sm: 2 },
                bgcolor: primaryBlue[500],
                color: "white",
                "&:hover": {
                  bgcolor: primaryBlue[600],
                },
              }}
            >
              <ArrowBack />
            </IconButton>
            <Typography
              variant={{ xs: "h5", sm: "h4" }}
              sx={{
                flexGrow: 1,
                fontWeight: 700,
                background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[800]})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              My Orders
            </Typography>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchMyOrders}
              disabled={loading}
              sx={{
                borderColor: primaryBlue[300],
                color: primaryBlue[600],
                "&:hover": {
                  borderColor: primaryBlue[500],
                  bgcolor: "rgba(0, 114, 206, 0.1)",
                },
              }}
            >
              Refresh
            </Button>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                ...glassStyles,
                bgcolor: "rgba(244, 67, 54, 0.9)",
                color: "white",
                fontWeight: 600,
              }}
            >
              {error}
            </Alert>
          )}

          {/* Loading State */}
          {loading ? (
            <Box
              sx={{
                ...glassStyles,
                p: 8,
                textAlign: "center",
              }}
            >
              <CircularProgress size={60} sx={{ color: primaryBlue[500] }} />
              <Typography variant="h6" sx={{ mt: 2, color: primaryBlue[700] }}>
                Loading your orders...
              </Typography>
            </Box>
          ) : orders.length === 0 ? (
            /* Empty State */
            <Box
              sx={{
                ...glassStyles,
                p: 8,
                textAlign: "center",
              }}
            >
              <ShoppingCart
                sx={{
                  fontSize: 120,
                  color: primaryBlue[300],
                  mb: 2,
                  opacity: 0.7,
                }}
              />
              <Typography
                variant="h5"
                gutterBottom
                sx={{ color: primaryBlue[700], fontWeight: 600 }}
              >
                No Orders Yet
              </Typography>
              <Typography
                variant="body1"
                sx={{ mb: 3, color: primaryBlue[600], opacity: 0.8 }}
              >
                You haven't placed any orders yet. Browse our items to get
                started!
              </Typography>
              <Button
                variant="contained"
                onClick={onBack}
                sx={{
                  px: 4,
                  py: 1.5,
                  background: `linear-gradient(45deg, ${primaryBlue[500]}, ${primaryBlue[700]})`,
                  boxShadow: `0 4px 20px ${primaryBlue[200]}`,
                  "&:hover": {
                    background: `linear-gradient(45deg, ${primaryBlue[600]}, ${primaryBlue[800]})`,
                    transform: "translateY(-2px)",
                    boxShadow: `0 6px 25px ${primaryBlue[300]}`,
                  },
                }}
              >
                Start Shopping
              </Button>
            </Box>
          ) : (
            /* Orders List */
            <Grid container spacing={3}>
              {orders.map((order) => {
                const statusInfo = getStatusInfo(order);
                const expanded = expandedOrders[order.id] || false;

                const toggleExpanded = () => {
                  setExpandedOrders(prev => ({
                    ...prev,
                    [order.id]: !prev[order.id]
                  }));
                };

                return (
                  <Grid item xs={12} key={order.id}>
                    <Card
                      elevation={0}
                      sx={{
                        ...cardGlassStyles,
                        overflow: "visible",
                      }}
                    >
                      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                        {/* Compact Order Header */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 2,
                            cursor: "pointer",
                          }}
                          onClick={toggleExpanded}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  color: primaryBlue[700],
                                  fontWeight: 600,
                                }}
                              >
                                Order #{formatOrderId(order.cartOrderId)}
                              </Typography>
                              {statusInfo.chip}
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{ color: primaryBlue[600], opacity: 0.8 }}
                            >
                              {getOrderSummary(order)} • {formatDate(order.createdAt)}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: "#00BFFF", fontWeight: 600, mt: 0.5 }}
                            >
                              Total: {formatPrice(order.totalAmount || order.grandTotal)}
                            </Typography>
                          </Box>
                          <IconButton 
                            sx={{ 
                              color: primaryBlue[600],
                              transition: "transform 0.2s",
                              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                            }}
                          >
                            <ExpandMore />
                          </IconButton>
                        </Box>

                        {/* Collapsible Content */}
                        <Collapse in={expanded}>
                          {/* Status Message */}
                          <Alert
                            severity={statusInfo.color}
                            sx={{
                              mb: 2,
                              ...glassStyles,
                              bgcolor: `rgba(${
                                statusInfo.color === "warning"
                                  ? "255, 152, 0"
                                  : statusInfo.color === "success"
                                  ? "76, 175, 80"
                                  : statusInfo.color === "error"
                                  ? "244, 67, 54"
                                  : "33, 150, 243"
                              }, 0.1)`,
                            }}
                          >
                            {statusInfo.message}
                          </Alert>

                        {/* Order Details */}
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="subtitle1"
                              gutterBottom
                              sx={{
                                color: primaryBlue[700],
                                fontWeight: 600,
                              }}
                            >
                              Order Details
                            </Typography>
                            <Box sx={{ mb: 2 }}>
                              {order.itemTotal > 0 && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    mb: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ color: primaryBlue[600] }}
                                  >
                                    Items Total:
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: primaryBlue[700],
                                      fontWeight: 500,
                                    }}
                                  >
                                    {formatPrice(order.itemTotal)}
                                  </Typography>
                                </Box>
                              )}
                              {order.packageTotal > 0 && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    mb: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ color: primaryBlue[600] }}
                                  >
                                    Packages Total:
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: primaryBlue[700],
                                      fontWeight: 500,
                                    }}
                                  >
                                    {formatPrice(order.packageTotal)}
                                  </Typography>
                                </Box>
                              )}
                              {order.registrationFee > 0 && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    mb: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{ color: primaryBlue[600] }}
                                  >
                                    Registration Fee:
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: primaryBlue[700],
                                      fontWeight: 500,
                                    }}
                                  >
                                    {formatPrice(order.registrationFee)}
                                  </Typography>
                                </Box>
                              )}
                              <Divider
                                sx={{ my: 1, bgcolor: primaryBlue[200] }}
                              />
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    color: primaryBlue[700],
                                    fontWeight: 600,
                                  }}
                                >
                                  Total Amount:
                                </Typography>
                                <Typography
                                  variant="h6"
                                  sx={{ color: "#00BFFF", fontWeight: 700 }}
                                >
                                  {formatPrice(
                                    order.totalAmount || order.grandTotal
                                  )}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <Typography
                              variant="subtitle1"
                              gutterBottom
                              sx={{
                                color: primaryBlue[700],
                                fontWeight: 600,
                              }}
                            >
                              Order Timeline
                            </Typography>
                            <Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  mb: 1,
                                }}
                              >
                                <Receipt
                                  sx={{
                                    mr: 1,
                                    fontSize: 20,
                                    color: primaryBlue[500],
                                  }}
                                />
                                <Box>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: primaryBlue[700],
                                      fontWeight: 500,
                                    }}
                                  >
                                    Order Placed
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: primaryBlue[600],
                                      opacity: 0.8,
                                    }}
                                  >
                                    {formatDate(order.createdAt)}
                                  </Typography>
                                </Box>
                              </Box>

                              {order.confirmedAt && (
                                <Box
                                  sx={{ display: "flex", alignItems: "center" }}
                                >
                                  <CheckCircle
                                    sx={{
                                      mr: 1,
                                      fontSize: 20,
                                      color: "#00BFFF",
                                    }}
                                  />
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: primaryBlue[700],
                                        fontWeight: 500,
                                      }}
                                    >
                                      Order Confirmed
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: primaryBlue[600],
                                        opacity: 0.8,
                                      }}
                                    >
                                      {formatDate(order.confirmedAt)}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}

                              {order.rejectedAt && (
                                <Box
                                  sx={{ display: "flex", alignItems: "center" }}
                                >
                                  <Cancel
                                    sx={{
                                      mr: 1,
                                      fontSize: 20,
                                      color: "#001A4D",
                                    }}
                                  />
                                  <Box>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: primaryBlue[700],
                                        fontWeight: 500,
                                      }}
                                    >
                                      Order Rejected
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: primaryBlue[600],
                                        opacity: 0.8,
                                      }}
                                    >
                                      {formatDate(order.rejectedAt)}
                                    </Typography>
                                  </Box>
                                </Box>
                              )}
                            </Box>
                          </Grid>
                        </Grid>

                        {/* Ordered Items Details */}
                        {order.items && order.items.length > 0 && (
                          <Box sx={{ mt: 3 }}>
                            <Typography
                              variant="subtitle1"
                              gutterBottom
                              sx={{
                                color: primaryBlue[700],
                                fontWeight: 600,
                                mb: 2,
                              }}
                            >
                              Ordered Items
                            </Typography>
                            <TableContainer
                              component={Paper}
                              variant="outlined"
                              sx={{
                                ...glassStyles,
                                bgcolor: "rgba(255, 255, 255, 0.6)",
                              }}
                            >
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell
                                      sx={{
                                        color: primaryBlue[700],
                                        fontWeight: 600,
                                      }}
                                    >
                                      Item
                                    </TableCell>
                                    <TableCell
                                      align="right"
                                      sx={{
                                        color: primaryBlue[700],
                                        fontWeight: 600,
                                      }}
                                    >
                                      Price
                                    </TableCell>
                                    <TableCell
                                      align="right"
                                      sx={{
                                        color: primaryBlue[700],
                                        fontWeight: 600,
                                      }}
                                    >
                                      Qty
                                    </TableCell>
                                    <TableCell
                                      align="right"
                                      sx={{
                                        color: primaryBlue[700],
                                        fontWeight: 600,
                                      }}
                                    >
                                      Total
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {order.items.map((item, index) => (
                                    <TableRow key={index}>
                                      <TableCell>
                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 2,
                                          }}
                                        >
                                          {item.imageUrl && (
                                            <img
                                              src={item.imageUrl}
                                              alt={item.name}
                                              style={{
                                                width: 40,
                                                height: 40,
                                                objectFit: "cover",
                                                borderRadius: 4,
                                              }}
                                            />
                                          )}
                                          <Box>
                                            <Typography
                                              variant="body2"
                                              fontWeight="medium"
                                              sx={{ color: primaryBlue[700] }}
                                            >
                                              {item.name}
                                            </Typography>
                                            {(item.selectedSize ||
                                              item.selectedColor) && (
                                              <Box sx={{ mt: 0.5 }}>
                                                {item.selectedSize && (
                                                  <Chip
                                                    label={`Size: ${item.selectedSize}`}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                      mr: 0.5,
                                                      fontSize: "0.7rem",
                                                      height: 18,
                                                    }}
                                                  />
                                                )}
                                                {item.selectedColor && (
                                                  <Chip
                                                    label={`Color: ${item.selectedColor}`}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{
                                                      fontSize: "0.7rem",
                                                      height: 18,
                                                    }}
                                                  />
                                                )}
                                              </Box>
                                            )}
                                          </Box>
                                        </Box>
                                      </TableCell>
                                      <TableCell
                                        align="right"
                                        sx={{ color: primaryBlue[700] }}
                                      >
                                        {formatPrice(item.sellingPrice)}
                                      </TableCell>
                                      <TableCell
                                        align="right"
                                        sx={{ color: primaryBlue[700] }}
                                      >
                                        {item.quantity}
                                      </TableCell>
                                      <TableCell
                                        align="right"
                                        sx={{
                                          color: primaryBlue[700],
                                          fontWeight: 600,
                                        }}
                                      >
                                        {formatPrice(
                                          item.sellingPrice * item.quantity
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        )}

                        {/* Ordered Packages Details */}
                        {order.packages && order.packages.length > 0 && (
                          <Box sx={{ mt: 3 }}>
                            <Typography
                              variant="subtitle1"
                              gutterBottom
                              sx={{
                                color: primaryBlue[700],
                                fontWeight: 600,
                                mb: 2,
                              }}
                            >
                              Ordered Packages
                            </Typography>
                            <TableContainer
                              component={Paper}
                              variant="outlined"
                              sx={{
                                ...glassStyles,
                                bgcolor: "rgba(255, 255, 255, 0.6)",
                              }}
                            >
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell
                                      sx={{
                                        color: primaryBlue[700],
                                        fontWeight: 600,
                                      }}
                                    >
                                      Package
                                    </TableCell>
                                    <TableCell
                                      align="right"
                                      sx={{
                                        color: primaryBlue[700],
                                        fontWeight: 600,
                                      }}
                                    >
                                      Price
                                    </TableCell>
                                    <TableCell
                                      align="right"
                                      sx={{
                                        color: primaryBlue[700],
                                        fontWeight: 600,
                                      }}
                                    >
                                      Qty
                                    </TableCell>
                                    <TableCell
                                      align="right"
                                      sx={{
                                        color: primaryBlue[700],
                                        fontWeight: 600,
                                      }}
                                    >
                                      Total
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {order.packages.map((pkg, index) => (
                                    <TableRow key={index}>
                                      <TableCell>
                                        <Typography
                                          variant="body2"
                                          fontWeight="medium"
                                          sx={{ color: primaryBlue[700] }}
                                        >
                                          {pkg.name}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: primaryBlue[600],
                                            opacity: 0.8,
                                          }}
                                        >
                                          {pkg.description}
                                        </Typography>
                                      </TableCell>
                                      <TableCell
                                        align="right"
                                        sx={{ color: primaryBlue[700] }}
                                      >
                                        {formatPrice(pkg.price)}
                                      </TableCell>
                                      <TableCell
                                        align="right"
                                        sx={{ color: primaryBlue[700] }}
                                      >
                                        {pkg.quantity}
                                      </TableCell>
                                      <TableCell
                                        align="right"
                                        sx={{
                                          color: primaryBlue[700],
                                          fontWeight: 600,
                                        }}
                                      >
                                        {formatPrice(pkg.price * pkg.quantity)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        )}

                        {/* Shipping Address */}
                        {order.shippingAddress && (
                          <Box sx={{ mt: 3 }}>
                            <Typography
                              variant="subtitle1"
                              gutterBottom
                              sx={{
                                color: primaryBlue[700],
                                fontWeight: 600,
                              }}
                            >
                              Shipping Address
                            </Typography>
                            <Box
                              sx={{
                                p: 2,
                                bgcolor: "rgba(255, 255, 255, 0.6)",
                                borderRadius: 2,
                                border: `1px solid ${primaryBlue[200]}`,
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  color: primaryBlue[700],
                                  fontWeight: 600,
                                }}
                              >
                                {order.shippingAddress.fullName}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: primaryBlue[600] }}
                              >
                                {order.shippingAddress.addressLine1}
                              </Typography>
                              {order.shippingAddress.addressLine2 && (
                                <Typography
                                  variant="body2"
                                  sx={{ color: primaryBlue[600] }}
                                >
                                  {order.shippingAddress.addressLine2}
                                </Typography>
                              )}
                              <Typography
                                variant="body2"
                                sx={{ color: primaryBlue[600] }}
                              >
                                {order.shippingAddress.city},{" "}
                                {order.shippingAddress.state} -{" "}
                                {order.shippingAddress.pincode}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: primaryBlue[600] }}
                              >
                                Phone: {order.shippingAddress.phone}
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {/* Payment Information */}
                        <Box
                          sx={{
                            mt: 3,
                            p: 2.5,
                            bgcolor: "rgba(255, 255, 255, 0.6)",
                            borderRadius: 2,
                            border: `1px solid ${primaryBlue[200]}`,
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            gutterBottom
                            sx={{
                              color: primaryBlue[700],
                              fontWeight: 600,
                            }}
                          >
                            Payment Information
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: primaryBlue[600] }}
                          >
                            Payment Method: UPI (infinitywhale@sbi)
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: primaryBlue[600] }}
                          >
                            Reference: Cart Order -{" "}
                            {formatOrderId(order.cartOrderId)}
                          </Typography>
                        </Box>

                        {/* Action Messages */}
                        {order.orderStatus === "pending" && (
                          <Box sx={{ mt: 3, textAlign: "center" }}>
                            <Alert
                              severity="info"
                              sx={{
                                ...glassStyles,
                                bgcolor: "rgba(0, 114, 206, 0.1)",
                              }}
                            >
                              <Typography variant="body2">
                                {order.paymentMethod === "cashfree" ||
                                order.paymentDetails?.cashfreePaymentId
                                  ? (
                                    <>
                                      <strong>Payment processing:</strong> If
                                      you completed Cashfree checkout, this
                                      order should show as confirmed within a
                                      minute. Refresh this page if needed.
                                    </>
                                  ) : (
                                    <>
                                      <strong>Payment Reminder:</strong> If you
                                      haven&apos;t completed the payment yet,
                                      please send{" "}
                                      <strong>
                                        {formatPrice(
                                          order.totalAmount || order.grandTotal
                                        )}
                                      </strong>{" "}
                                      to UPI ID:{" "}
                                      <strong>infinitywhale@sbi</strong> with
                                      reference &quot;Cart Order -{" "}
                                      {formatOrderId(order.cartOrderId)}&quot;
                                    </>
                                  )}
                              </Typography>
                            </Alert>
                          </Box>
                        )}

                        {order.orderStatus === "confirmed" && (
                          <Box sx={{ mt: 3, textAlign: "center" }}>
                            <Alert
                              severity="success"
                              sx={{
                                ...glassStyles,
                                bgcolor: "rgba(76, 175, 80, 0.1)",
                              }}
                            >
                              <Typography variant="body2">
                                🎉 <strong>Congratulations!</strong> Your order
                                is confirmed and your items will be shipped to
                                the provided address soon.
                              </Typography>
                            </Alert>
                          </Box>
                        )}
                        </Collapse>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Container>

        {/* Footer Component */}
        <Footer primaryBlue={primaryBlue} glassStyles={glassStyles} />
      </Box>
    </Box>
  );
};

export default MyOrders;
